import { useEffect, useRef, useState, type CSSProperties } from "react";
import { formatDecimalPlaces } from "../../../../helpers/format-decimal-places";
import { SalesHistoryFilters } from "../../../../hooks/agencies-sales-details/use-get-agencies-sales-details";
import { useGetAgencySellerStatement } from "../../../../hooks/agencies-sales-details/use-get-agency-seller-statement";
import { StatementRow } from "../../../../services/agencies-sales-details/types";
import { SaleHistoryItem } from "../../../../services/agencies-sales-details/types";
import { useUserTimezone } from "../../../../hooks/date/use-user-timezone";
import { useReverseAgencySaleDetail } from "../../../../hooks/agencies-sales-details/use-reverse-agency-sale-detail";
import { reverseErrorMessage } from "../../../../helpers/sale-error-message";
import { useGetAgenciesSalesSummary } from "../../../../hooks/agencies-sales-details/use-get-agencies-sales-summary";
import { useGetMyProfile } from "../../../../hooks/profile/use-get-my-profile";
import {
  exportSalesHistoryPdf,
  exportSalesHistoryXlsx,
  saleValueBrl,
} from "../../../../helpers/export-sales-history";
import { SalesHistoryPricing } from "../../../../services/agencies-sales-details/types";

const fmtBrl = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
import { t } from "../../../../i18n";

const AVATAR_FALLBACK = "https://didderlive.com/assets/didder-logo.png";

const HistoryRow = ({
  item,
  formatDate,
  onReverse,
  pricing,
  balanceAfter = null,
  canReverse = true,
}: {
  item: SaleHistoryItem;
  formatDate: (value?: string | null) => string;
  onReverse: (item: SaleHistoryItem) => void;
  pricing: SalesHistoryPricing | null;
  balanceAfter?: number | null;
  canReverse?: boolean;
}) => {
  const valueBrl = saleValueBrl(Number(item?.coins), pricing);
  const title = item?.userUsername || `Usuário #${item?.userId}`;
  const isReversed = !!item?.reversedAt;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: isReversed ? "#faf7f7" : "#fff",
        borderRadius: "10px",
        padding: "14px 16px",
        marginBottom: "10px",
        gap: "12px",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        // A estornada continua na lista, mas fica visivelmente apagada: ela não
        // conta mais no total vendido.
        opacity: isReversed ? 0.72 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <img
          src={item?.userImgUser || AVATAR_FALLBACK}
          onError={(e) => {
            e.currentTarget.src = AVATAR_FALLBACK;
          }}
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "21px",
            objectFit: "cover",
            flexShrink: 0,
            background: "#eee",
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#222",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>

          <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>
            ID {item?.userId}
          </div>

          <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>
            {formatDate(item?.dateSentCoin ?? item?.createdAt)}
          </div>

          {balanceAfter != null && (
            <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
              {t("history.row.balance", { value: formatDecimalPlaces(balanceAfter) })}
            </div>
          )}

          {isReversed && (
            <div
              style={{
                display: "inline-block",
                marginTop: "6px",
                padding: "2px 8px",
                borderRadius: "10px",
                background: "#fdecea",
                color: "#b3261e",
                fontSize: "11px",
                fontWeight: "bold",
              }}
            >
              Estornada em {formatDate(item?.reversedAt)}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            color: isReversed ? "#999" : "#2e7d32",
            whiteSpace: "nowrap",
            textDecoration: isReversed ? "line-through" : "none",
          }}
        >
          +{formatDecimalPlaces(item?.coins)}
          {valueBrl != null && (
            <div style={{ fontSize: "12px", fontWeight: "normal", color: "#777", textAlign: "right" }}>
              {fmtBrl(valueBrl)}
            </div>
          )}
        </div>

        {/* Estornar. Some quando a venda já foi estornada — estorno é uma vez só. */}
        {!isReversed && canReverse && (
          <button
            type="button"
            onClick={() => onReverse(item)}
            title="Estornar esta venda"
            aria-label={`Estornar a venda de ${formatDecimalPlaces(
              item?.coins
            )} moedas para ${title}`}
            style={{
              // 44px é o alvo de toque mínimo confortável no celular — com 30px
              // o dedo erra a linha e acaba clicando no card.
              width: "44px",
              height: "44px",
              borderRadius: "22px",
              border: "1.5px solid #e79a94",
              // fundo preenchido pra ler como BOTÃO, não como um traço de texto
              background: "#fdecea",
              color: "#b3261e",
              fontSize: "26px",
              lineHeight: "1",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              flexShrink: 0,
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
            }}
          >
            −
          </button>
        )}
      </div>
    </div>
  );
};

// Statement sale row -> the shape the sale row (and the reversal modal) uses.
const statementToSale = (row: StatementRow): SaleHistoryItem =>
  ({
    id: row.saleId ?? 0,
    userId: row.buyerId ?? 0,
    userUsername: row.buyerUsername ?? "",
    userImgUser: row.buyerImgUser ?? "",
    coins: Math.abs(row.coinsDelta),
    dateSentCoin: row.at,
    createdAt: row.at,
    reversedAt: row.reversedAt ?? null,
  }) as unknown as SaleHistoryItem;

const MOVEMENT_STYLE: Record<string, { bg: string; badgeBg: string; badgeFg: string }> = {
  conversion: { bg: "#f1f8f1", badgeBg: "#e0f2e1", badgeFg: "#1b5e20" },
  reversal: { bg: "#fff8ef", badgeBg: "#ffe9cc", badgeFg: "#8a4b00" },
  other: { bg: "#f8f3fb", badgeBg: "#efe2f6", badgeFg: "#5e2a7e" },
  unrecorded: { bg: "#fff4f4", badgeBg: "#fde0e0", badgeFg: "#a01818" },
};

// Conversions, reversals and other stock movements: visibly different from sales
// and never counted as "sold".
const MovementRow = ({
  row,
  formatDate,
  balanceMode,
}: {
  row: StatementRow;
  formatDate: (value?: string | null) => string;
  balanceMode: boolean;
}) => {
  const style = MOVEMENT_STYLE[row.kind] ?? MOVEMENT_STYLE.other;
  const detail =
    row.kind === "conversion"
      ? t("history.row.conversion", {
          coins: formatDecimalPlaces(row.coinsDelta),
          points: formatDecimalPlaces(row.pointsSpent ?? 0),
        })
      : row.kind === "reversal"
        ? `${t("history.row.reversalOf", { id: row.saleId ?? "?" })}${row.buyerUsername ? ` · ${row.buyerUsername}` : ""}`
        : row.kind === "unrecorded"
          ? t("history.row.unrecorded")
          : row.description ?? "";
  const positive = row.coinsDelta >= 0;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        background: style.bg,
        border: `1px dashed ${style.badgeBg}`,
        borderRadius: "10px",
        padding: "12px 16px",
        marginBottom: "10px",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "10px",
            background: style.badgeBg,
            color: style.badgeFg,
            fontSize: "11px",
            fontWeight: "bold",
          }}
        >
          {t(`history.kind.${row.kind}` as any)}
        </span>
        <div style={{ fontSize: "13px", color: "#333", marginTop: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {detail}
        </div>
        <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>{formatDate(row.at)}</div>
        {balanceMode && row.balanceAfter != null && (
          <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
            {t("history.row.balance", { value: formatDecimalPlaces(row.balanceAfter) })}
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          whiteSpace: "nowrap",
          color: positive ? "#1b5e20" : "#b3261e",
        }}
      >
        {positive ? "+" : "−"}
        {formatDecimalPlaces(Math.abs(row.coinsDelta))}
      </div>
    </div>
  );
};

// "YYYY-MM-DD" (date input, seller's own day) -> "DD/MM/YYYY"
const formatDay = (value: string) => value.split("-").reverse().join("/");

const labelStyle: CSSProperties = {
  fontSize: "12px",
  fontWeight: "bold",
  color: "#555",
  marginBottom: "4px",
  display: "block",
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "10px",
  background: "#f0f3fc",
  border: "1px solid #ccc",
  borderRadius: "6px",
  fontSize: "14px",
  boxSizing: "border-box",
};

export const SalesHistory = () => {
  const { formatInZone, zonedBoundToUtc, todayInZone, zone } = useUserTimezone();

  const [userIdInput, setUserIdInput] = useState("");
  const [dateInitial, setDateInitial] = useState("");
  const [dateFinal, setDateFinal] = useState("");
  const [filters, setFilters] = useState<SalesHistoryFilters>({});
  // Human description of the APPLIED filter (seller's own day/zone), for the
  // totals card and the downloaded files.
  const [filterLabel, setFilterLabel] = useState(t("history.filter.all"));
  const [appliedDays, setAppliedDays] = useState<{ from?: string; to?: string }>({});
  const [downloading, setDownloading] = useState<"xlsx" | "pdf" | null>(null);
  const [downloadFeedback, setDownloadFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const { user } = useGetMyProfile();
  const { summary, isLoading: isSummaryLoading } = useGetAgenciesSalesSummary(filters);
  const pricing = summary?.pricing ?? null;

  // Venda escolhida no "−", aguardando confirmação no modal.
  const [toReverse, setToReverse] = useState<SaleHistoryItem | null>(null);
  const [feedback, setFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const { reverse, isLoading: isReversing } = useReverseAgencySaleDetail();

  // Sales, conversions, reversals and every other stock movement, newest first.
  const {
    rows,
    balanceMode,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAgencySellerStatement(filters);
  const sales = rows;

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // scroll infinito: quando a sentinela (fim da lista) entra na viewport,
  // busca a próxima página. rootMargin adianta o carregamento.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = () => {
    const trimmed = userIdInput.trim();
    const parsedId = trimmed ? Number(trimmed) : undefined;

    setFilters({
      userId:
        parsedId !== undefined && Number.isFinite(parsedId)
          ? parsedId
          : undefined,
      // datas escolhidas no fuso do usuário -> limites UTC (comparáveis ao created_at)
      dateInitial: zonedBoundToUtc(dateInitial, "00:00:00", false),
      dateFinal: zonedBoundToUtc(dateFinal, "23:59:59", true),
    });
    const parts: string[] = [];
    if (parsedId !== undefined && Number.isFinite(parsedId)) {
      parts.push(t("history.filter.user", { id: parsedId }));
    }
    if (dateInitial) parts.push(t("history.filter.from", { date: formatDay(dateInitial) }));
    if (dateFinal) parts.push(t("history.filter.to", { date: formatDay(dateFinal) }));
    setFilterLabel(parts.length ? parts.join(" · ") : t("history.filter.all"));
    setAppliedDays({ from: dateInitial || undefined, to: dateFinal || undefined });
    setDownloadFeedback(null);
  };

  const handleDownload = async (kind: "xlsx" | "pdf") => {
    if (downloading) return;
    setDownloading(kind);
    setDownloadFeedback(null);
    try {
      const ctx = {
        filters,
        filterLabel,
        agencyName: user?.agency?.name ?? `#${user?.agency?.id ?? user?.id ?? ""}`,
        zone,
        dayFrom: appliedDays.from,
        dayTo: appliedDays.to,
      };
      const count =
        kind === "xlsx" ? await exportSalesHistoryXlsx(ctx) : await exportSalesHistoryPdf(ctx);
      setDownloadFeedback({ ok: true, text: t("history.download.done", { count }) });
    } catch {
      setDownloadFeedback({ ok: false, text: t("history.download.error") });
    } finally {
      setDownloading(null);
    }
  };

  // Confirma o estorno. A API é quem decide: se o cliente não tiver mais o valor
  // completo na carteira, ela recusa e NADA é alterado — a mensagem devolvida diz
  // isso com todas as letras.
  const handleConfirmReverse = async () => {
    if (!toReverse || isReversing) {
      return;
    }

    try {
      const response = await reverse({ saleId: toReverse.id });

      if (response?.status === "success" && response?.data?.reversed) {
        setFeedback({
          ok: true,
          text: `Venda de ${formatDecimalPlaces(
            toReverse.coins
          )} moedas estornada. As moedas voltaram para o seu estoque.`,
        });
      } else {
        setFeedback({ ok: false, text: reverseErrorMessage(response?.error) });
      }
    } catch {
      setFeedback({ ok: false, text: reverseErrorMessage() });
    }

    setToReverse(null);
  };

  const handleClear = () => {
    setUserIdInput("");
    setDateInitial("");
    setDateFinal("");
    setFilters({});
    setFilterLabel(t("history.filter.all"));
    setAppliedDays({});
    setDownloadFeedback(null);
  };

  return (
    <div>
      {/* filtros: User ID + intervalo de data */}
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "14px 16px",
          marginBottom: "14px",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div style={{ marginBottom: "10px" }}>
          <label style={labelStyle}>User ID</label>
          <input
            type="number"
            inputMode="numeric"
            placeholder="ID do usuário (opcional)"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={labelStyle}>De</label>
            <input
              type="date"
              value={dateInitial}
              max={dateFinal || todayInZone()}
              onChange={(e) => setDateInitial(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={labelStyle}>Até</label>
            <input
              type="date"
              value={dateFinal}
              min={dateInitial || undefined}
              max={todayInZone()}
              onChange={(e) => setDateFinal(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleSearch}
            style={{
              flex: 1,
              background: "#3f51b5",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "10px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Pesquisar
          </button>

          <button
            onClick={handleClear}
            style={{
              background: "transparent",
              color: "#3f51b5",
              border: "1px solid #3f51b5",
              borderRadius: "6px",
              padding: "10px 16px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Limpar
          </button>
        </div>
      </div>

      {/* resultado do último estorno */}
      {feedback && (
        <div
          style={{
            background: feedback.ok ? "#e8f5e9" : "#fdecea",
            color: feedback.ok ? "#1b5e20" : "#b3261e",
            border: `1px solid ${feedback.ok ? "#c8e6c9" : "#f5c6c3"}`,
            borderRadius: "10px",
            padding: "12px 14px",
            marginBottom: "12px",
            fontSize: "13px",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <span style={{ flex: 1 }}>{feedback.text}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            aria-label="Fechar aviso"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: "16px",
              cursor: "pointer",
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* total of the WHOLE filtered list (server-side) + downloads */}
      <div
        style={{
          background: "linear-gradient(135deg, #3f51b5, #5c6bc0)",
          color: "#fff",
          borderRadius: "10px",
          padding: "14px 16px",
          marginBottom: "12px",
        }}
      >
        <div style={{ fontSize: "12px", opacity: 0.9 }}>
          {t("history.summary.title")} · {filterLabel}
        </div>
        {isSummaryLoading || !summary ? (
          <div style={{ fontSize: "14px", marginTop: "6px" }}>
            {t("history.summary.loading")}
          </div>
        ) : (
          <>
            <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "4px" }}>
              {t("history.summary.line", {
                coins: formatDecimalPlaces(summary.total),
                count: summary.count,
              })}
            </div>
            {pricing && (
              <div style={{ fontSize: "14px", marginTop: "2px" }}>
                {t("history.summary.valueLine", {
                  value: fmtBrl(saleValueBrl(summary.total, pricing) ?? 0),
                })}
              </div>
            )}
            {summary.statement && summary.statement.movements.conversions.count > 0 && (
              <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>
                {t("history.summary.conversionsLine", {
                  coins: formatDecimalPlaces(summary.statement.movements.conversions.coins),
                  count: summary.statement.movements.conversions.count,
                })}
              </div>
            )}
            {summary.statement?.balanceMode && summary.statement.opening != null && summary.statement.closing != null && (
              <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>
                {t("history.summary.balanceLine", {
                  opening: formatDecimalPlaces(summary.statement.opening),
                  closing: formatDecimalPlaces(summary.statement.closing),
                })}
              </div>
            )}
            {summary.reversedCount > 0 && (
              <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>
                {t("history.summary.reversedLine", {
                  coins: formatDecimalPlaces(summary.reversedTotal),
                  count: summary.reversedCount,
                })}
              </div>
            )}
          </>
        )}
        <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
          {(["xlsx", "pdf"] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              disabled={!!downloading}
              onClick={() => handleDownload(kind)}
              style={{
                flex: 1,
                height: "40px",
                background: "#fff",
                color: "#3f51b5",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: downloading ? "default" : "pointer",
                opacity: downloading && downloading !== kind ? 0.6 : 1,
              }}
            >
              {downloading === kind
                ? t("history.download.working")
                : t(kind === "xlsx" ? "history.download.xlsx" : "history.download.pdf")}
            </button>
          ))}
        </div>
        {downloadFeedback && (
          <div role="status" style={{ fontSize: "12px", marginTop: "8px" }}>
            {downloadFeedback.text}
          </div>
        )}
      </div>

      {/* lista */}
      {isLoading ? (
        <div style={{ color: "#777", padding: "10px 0" }}>Carregando...</div>
      ) : !sales.length ? (
        <div style={{ color: "#777", padding: "10px 0" }}>
          Nenhuma venda encontrada
        </div>
      ) : (
        rows.map((row) =>
          row.kind === "sale" ? (
            <HistoryRow
              key={row.id}
              item={statementToSale(row)}
              formatDate={formatInZone}
              pricing={pricing}
              balanceAfter={balanceMode ? row.balanceAfter : null}
              canReverse={row.saleId != null}
              onReverse={(sale) => {
                setFeedback(null);
                setToReverse(sale);
              }}
            />
          ) : (
            <MovementRow key={row.id} row={row} formatDate={formatInZone} balanceMode={balanceMode} />
          )
        )
      )}

      {/* totals of the WHOLE filter at the bottom of the list */}
      {!isLoading && !hasNextPage && sales.length > 0 && summary && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            background: "#e8eaf6",
            border: "1px solid #c5cae9",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "10px",
            fontSize: "14px",
          }}
        >
          <div>
            <div style={{ fontWeight: "bold", color: "#283593" }}>
              {t("history.list.totalsTitle")}
            </div>
            <div style={{ fontSize: "12px", color: "#555" }}>
              {t("history.summary.salesCount", { count: summary.count })}
            </div>
            {summary.statement && summary.statement.movements.conversions.count > 0 && (
              <div style={{ fontSize: "12px", color: "#1b5e20" }}>
                {t("history.summary.conversionsLine", {
                  coins: formatDecimalPlaces(summary.statement.movements.conversions.coins),
                  count: summary.statement.movements.conversions.count,
                })}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: "bold", color: "#2e7d32", fontSize: "16px" }}>
              {formatDecimalPlaces(summary.total)}
            </div>
            {pricing && (
              <div style={{ fontSize: "12px", color: "#555" }}>
                {fmtBrl(saleValueBrl(summary.total, pricing) ?? 0)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* sentinela do scroll infinito */}
      <div ref={sentinelRef} style={{ height: "1px" }} />

      {isFetchingNextPage && (
        <div
          style={{ color: "#777", padding: "10px 0", textAlign: "center" }}
        >
          Carregando mais...
        </div>
      )}

      {!isLoading && !hasNextPage && sales.length > 0 && (
        <div
          style={{
            color: "#aaa",
            padding: "10px 0",
            textAlign: "center",
            fontSize: "12px",
          }}
        >
          Fim do histórico
        </div>
      )}

      {/* modal de confirmação do estorno */}
      {toReverse && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirmar estorno da venda"
          onClick={() => !isReversing && setToReverse(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "20px",
              width: "100%",
              maxWidth: "360px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                color: "#222",
                marginBottom: "10px",
              }}
            >
              Estornar esta venda?
            </div>

            <div
              style={{ fontSize: "14px", color: "#555", lineHeight: "1.5" }}
            >
              <b>{formatDecimalPlaces(toReverse.coins)} moedas</b> serão
              retiradas de{" "}
              <b>{toReverse.userUsername || `Usuário #${toReverse.userId}`}</b>{" "}
              (ID {toReverse.userId}) e devolvidas ao seu estoque.
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#777",
                marginTop: "10px",
                lineHeight: "1.5",
              }}
            >
              Se o cliente já tiver gasto parte das moedas, o estorno não é
              feito — é tudo ou nada. A venda continua no histórico, marcada
              como estornada, e deixa de contar no total vendido.
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "18px",
              }}
            >
              <button
                type="button"
                onClick={() => setToReverse(null)}
                disabled={isReversing}
                style={{
                  flex: 1,
                  background: "transparent",
                  color: "#3f51b5",
                  border: "1px solid #3f51b5",
                  borderRadius: "6px",
                  padding: "10px",
                  fontSize: "14px",
                  cursor: isReversing ? "default" : "pointer",
                  opacity: isReversing ? 0.6 : 1,
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleConfirmReverse}
                disabled={isReversing}
                style={{
                  flex: 1,
                  background: "#b3261e",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: isReversing ? "default" : "pointer",
                  opacity: isReversing ? 0.7 : 1,
                }}
              >
                {isReversing ? "Estornando..." : "Estornar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
